import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { apiService, ApiError } from '../../services/api';
import { TradeLineInventoryResponse, AdminRefreshResponse, SalesStats, UserListItem } from '../../types/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Loader2, RefreshCw, Database, Users, Settings, Zap, ArrowLeft, AlertTriangle, TrendingUp, TrendingDown, Minus, Check, ChevronDown, X, Menu, LogOut, FileText, Package } from 'lucide-react';
import { UserList } from '../manager/UserList';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { DisputesReview } from '../manager/DisputesReview';
import { TradelineOrdersAdmin } from './TradelineOrdersAdmin';

type ActiveSection = 'sales' | 'users' | 'inventory' | 'disputes' | 'tradeline-orders';

export function AdminPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get manager info from localStorage
  const managerSession = localStorage.getItem('managerSession');
  const managerData = managerSession ? JSON.parse(managerSession) : null;
  const managerRole = managerData?.role || 'manager';
  const isAdmin = managerRole === 'admin';
  const managerName = managerData ? `${managerData.firstName} ${managerData.lastName}` : '';
  
  // Set initial active section based on role or URL parameter
  const getInitialSection = (): ActiveSection => {
    const params = new URLSearchParams(location.search);
    const userIdParam = params.get('userId');
    if (userIdParam) {
      return 'users';
    }
    const tabParam = params.get('tab') as ActiveSection | null;
    if (tabParam && ['sales', 'users', 'inventory', 'disputes', 'tradeline-orders'].includes(tabParam)) {
      return tabParam;
    }
    return 'users'; // Default to users for both admin and manager
  };
  
  // Determine if running on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const defaultEntity = isLocalhost ? 'STAGING' : 'CREDITBOOSTER';
  const alternateEntity = 'CREDITIER';
  
  const [, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<ActiveSection>(getInitialSection());
  const [refreshTriggers, setRefreshTriggers] = useState<Record<ActiveSection, number>>({
    users: 0, disputes: 0, sales: 0, inventory: 0, 'tradeline-orders': 0,
  });

  const handleSectionChange = (section: ActiveSection) => {
    setActiveSection(section);
    setSearchParams({ tab: section }, { replace: true });
    setRefreshTriggers(prev => ({ ...prev, [section]: prev[section] + 1 }));
  };
  const [salesStats, setSalesStats] = useState<SalesStats | null>(null);
  const [inventory, setInventory] = useState<TradeLineInventoryResponse | null>(null);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [showProviderFilter, setShowProviderFilter] = useState(false);
  const [entityFilter, setEntityFilter] = useState<'CREDITBOOSTER' | 'CREDITIER' | 'STAGING' | 'ALL'>(defaultEntity as any);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const targetUserId = new URLSearchParams(location.search).get('userId') || '';
  const handledTargetUserIdRef = useRef<string | null>(null);
  const [isRedirectingToUser, setIsRedirectingToUser] = useState(!!targetUserId);

  const isAmexTradeline = (provider?: string, cardName?: string): boolean => {
    const normalized = `${provider || ''} ${cardName || ''}`.toLowerCase();
    return normalized.includes('amex') || normalized.includes('american express');
  };

  const handleLogout = () => {
    // Clear manager session and tokens
    localStorage.removeItem('managerSession');
    localStorage.removeItem('manager');
    apiService.logout(); // This clears all tokens
    
    // Navigate to login page
    navigate('/login');
  };

  const fetchSalesStats = async () => {
    try {
      setIsLoadingSales(true);
      setError('');
      const filterValue = entityFilter === 'ALL' ? undefined : entityFilter;
      const response = await apiService.getSalesStats(filterValue);
      setSalesStats(response.stats);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch sales stats');
    } finally {
      setIsLoadingSales(false);
    }
  };

  const fetchInventory = async () => {
    try {
      setIsLoadingInventory(true);
      setError('');
      const data = await apiService.getTradeLineInventory();
      setInventory(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch inventory');
    } finally {
      setIsLoadingInventory(false);
    }
  };

  const syncInventory = async () => {
    setShowSyncConfirm(false);
    try {
      setIsSyncing(true);
      setError('');
      setSuccess('');
      const response = await apiService.syncTradeLineInventory();
      setSuccess(`Inventory sync completed: ${response.message}`);
      // Refresh inventory after sync
      await fetchInventory();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to sync inventory');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleUserSelect = async (user: UserListItem) => {
    try {
      setError('');
      setSuccess('');

      const response = await apiService.managerLoginAsUser(user.phoneNumber);
      sessionStorage.setItem('user', JSON.stringify(response.user));

      setTimeout(() => {
        window.location.href = `/users/${response.user.id}?tab=analytics`;
      }, 500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to login as user');
    }
  };

  useEffect(() => {
    fetchSalesStats();
    fetchInventory();
  }, [entityFilter]);

  useEffect(() => {
    if (refreshTriggers.sales === 0) return;
    const refreshSalesSilently = async () => {
      try {
        const filterValue = entityFilter === 'ALL' ? undefined : entityFilter;
        const response = await apiService.getSalesStats(filterValue);
        setSalesStats(response.stats);
      } catch { /* keep stale data on error */ }
    };
    refreshSalesSilently();
  }, [refreshTriggers.sales]);

  useEffect(() => {
    if (refreshTriggers.inventory === 0) return;
    const refreshInventorySilently = async () => {
      try {
        const data = await apiService.getTradeLineInventory();
        setInventory(data);
      } catch { /* keep stale data on error */ }
    };
    refreshInventorySilently();
  }, [refreshTriggers.inventory]);

  useEffect(() => {
    if (targetUserId && activeSection !== 'users') {
      setActiveSection('users');
    }
  }, [targetUserId, activeSection]);

  useEffect(() => {
    if (!targetUserId) {
      handledTargetUserIdRef.current = null;
      return;
    }
    if (handledTargetUserIdRef.current === targetUserId) return;

    handledTargetUserIdRef.current = targetUserId;
    const tabParam = new URLSearchParams(location.search).get('tab');
    window.location.href = tabParam
      ? `/users/${targetUserId}?tab=${tabParam}`
      : `/users/${targetUserId}`;
  }, [targetUserId]);

  // Close provider filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showProviderFilter && !target.closest('.provider-filter-container')) {
        setShowProviderFilter(false);
      }
    };

    if (showProviderFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProviderFilter]);

  // Filter inventory by selected providers
  const filteredInventory = inventory?.inventory.filter(item => {
    if (selectedProviders.length === 0) return true;
    return selectedProviders.includes(item.provider);
  }) || [];

  // Toggle provider selection
  const toggleProvider = (provider: string) => {
    setSelectedProviders(prev => 
      prev.includes(provider)
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        {/* Top Row: Logo and User Info */}
        <div className="px-6 h-14 flex items-center justify-between border-b border-gray-200">
          {/* Left: Logo */}
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="CreditBooster.ai" className="w-7 h-7" />
            <span className="text-base font-semibold text-gray-900">CreditBooster.ai</span>
          </div>

          {/* Right: User Info with Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 hover:bg-gray-50 rounded-md px-2 py-1 transition-colors">
                {managerName && (
                  <>
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-medium text-gray-900 leading-tight">
                        {managerName}
                      </span>
                      <span className="text-xs text-gray-500 leading-tight">
                        {managerRole}
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-500 self-start" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Bottom Row: Navigation Menu */}
        <div className="px-6">
          <nav className="flex items-center gap-6" style={{ height: '72px' }}>
            <button
              onClick={() => handleSectionChange('users')}
              className={`flex items-center gap-2 px-4 py-2 text-base transition-colors rounded-lg ${
                activeSection === 'users'
                  ? 'text-gray-900 bg-gray-100 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium'
              }`}
            >
              <Users className="h-4 w-4" />
              Users
            </button>
            <button
              onClick={() => handleSectionChange('disputes')}
              className={`flex items-center gap-2 px-4 py-2 text-base transition-colors rounded-lg ${
                activeSection === 'disputes'
                  ? 'text-gray-900 bg-gray-100 font-semibold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium'
              }`}
            >
              <FileText className="h-4 w-4" />
              Disputes Review
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => handleSectionChange('sales')}
                  className={`flex items-center gap-2 px-4 py-2 text-base transition-colors rounded-lg ${
                    activeSection === 'sales'
                      ? 'text-gray-900 bg-gray-100 font-semibold'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium'
                  }`}
                >
                  <TrendingUp className="h-4 w-4" />
                  Sales
                </button>
                <button
                  onClick={() => handleSectionChange('inventory')}
                  className={`flex items-center gap-2 px-4 py-2 text-base transition-colors rounded-lg ${
                    activeSection === 'inventory'
                      ? 'text-gray-900 bg-gray-100 font-semibold'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium'
                  }`}
                >
                  <Database className="h-4 w-4" />
                  Tradeline Inventory
                </button>
                <button
                  onClick={() => handleSectionChange('tradeline-orders')}
                  className={`flex items-center gap-2 px-4 py-2 text-base transition-colors rounded-lg ${
                    activeSection === 'tradeline-orders'
                      ? 'text-gray-900 bg-gray-100 font-semibold'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium'
                  }`}
                >
                  <Package className="h-4 w-4" />
                  Tradeline Orders
                </button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="pl-6 pt-4 pr-6 pb-6 space-y-6">
        {activeSection === 'sales' && (
          <div className="space-y-6">
            {/* Filter Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="inline-flex rounded-lg border border-gray-200 bg-background p-1">
                  <button
                    onClick={() => setEntityFilter(defaultEntity as any)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      entityFilter === defaultEntity
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {defaultEntity}
                  </button>
                  <button
                    onClick={() => setEntityFilter(alternateEntity)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      entityFilter === alternateEntity
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {alternateEntity}
                  </button>
                  <button
                    onClick={() => setEntityFilter('ALL')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      entityFilter === 'ALL'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    ALL
                  </button>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchSalesStats}
                disabled={isLoadingSales}
              >
                {isLoadingSales ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>

            {isLoadingSales ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : salesStats ? (
              <>
                {/* Active Users — Plan Breakdown Blocks */}
                {(() => {
                  const freeCount = salesStats.productBreakdown.FREE || 0;
                  const proCount = salesStats.productBreakdown.PRO_ACCESS_20 || 0;
                  const fullCount = salesStats.productBreakdown.FULL_ACCESS_100 || 0;
                  const total = freeCount + proCount + fullCount;
                  const pct = (n: number) => total > 0 ? ((n / total) * 100).toFixed(1) : '0.0';

                  const plans = [
                    { label: 'Free', count: freeCount, color: '#6b7280', bgLight: 'bg-gray-50', price: '$0/mo' },
                    { label: 'Pro Access', count: proCount, color: '#3b82f6', bgLight: 'bg-blue-50', price: '$20/mo' },
                    { label: 'Full Access', count: fullCount, color: '#8b5cf6', bgLight: 'bg-violet-50', price: '$100/mo' },
                  ];

                  return (
                    <div className="space-y-4">
                      <div className="flex items-baseline gap-3">
                        <h3 className="text-lg font-semibold text-foreground">Active Users</h3>
                        <span className="text-2xl font-bold text-foreground">{total.toLocaleString()}</span>
                        <span className="text-sm text-muted-foreground">across all plans</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {plans.map((plan) => {
                          const percentage = parseFloat(pct(plan.count));
                          return (
                            <Card key={plan.label} className={`relative overflow-hidden ${plan.bgLight} border`}>
                              <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: plan.color }} />
                              <CardContent className="pt-5 pb-5 pl-5 pr-5">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <p className="text-sm font-medium text-muted-foreground">{plan.label}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{plan.price}</p>
                                  </div>
                                  <span
                                    className="text-xs font-semibold px-2 py-0.5 rounded-full text-white"
                                    style={{ backgroundColor: plan.color }}
                                  >
                                    {percentage}%
                                  </span>
                                </div>
                                <p className="text-3xl font-bold tracking-tight" style={{ color: plan.color }}>
                                  {plan.count.toLocaleString()}
                                </p>
                                <div className="mt-3 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ width: `${percentage}%`, backgroundColor: plan.color }}
                                  />
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Main Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {/* Total Users */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Total Users</CardDescription>
                      <CardTitle className="text-3xl">{salesStats.totalUsers.toLocaleString()}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Week</span>
                          <div className="flex items-center">
                            {salesStats.weekDelta.totalUsers > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                            ) : salesStats.weekDelta.totalUsers < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={salesStats.weekDelta.totalUsers > 0 ? 'text-green-600' : salesStats.weekDelta.totalUsers < 0 ? 'text-red-600' : 'text-gray-600'}>
                              {salesStats.weekDelta.totalUsers > 0 ? '+' : ''}{salesStats.weekDelta.totalUsers}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Month</span>
                          <div className="flex items-center">
                            {salesStats.monthDelta.totalUsers > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                            ) : salesStats.monthDelta.totalUsers < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={salesStats.monthDelta.totalUsers > 0 ? 'text-green-600' : salesStats.monthDelta.totalUsers < 0 ? 'text-red-600' : 'text-gray-600'}>
                              {salesStats.monthDelta.totalUsers > 0 ? '+' : ''}{salesStats.monthDelta.totalUsers}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Deleted Users */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Deleted Users</CardDescription>
                      <CardTitle className="text-3xl">{(salesStats.deletedUsers || 0).toLocaleString()}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Week</span>
                          <div className="flex items-center">
                            {(salesStats.weekDelta?.deletedUsers || 0) > 0 ? (
                              <TrendingUp className="h-4 w-4 text-red-600 mr-1" />
                            ) : (salesStats.weekDelta?.deletedUsers || 0) < 0 ? (
                              <TrendingDown className="h-4 w-4 text-green-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={(salesStats.weekDelta?.deletedUsers || 0) > 0 ? 'text-red-600' : (salesStats.weekDelta?.deletedUsers || 0) < 0 ? 'text-green-600' : 'text-gray-600'}>
                              {(salesStats.weekDelta?.deletedUsers || 0) > 0 ? '+' : ''}{salesStats.weekDelta?.deletedUsers || 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Month</span>
                          <div className="flex items-center">
                            {(salesStats.monthDelta?.deletedUsers || 0) > 0 ? (
                              <TrendingUp className="h-4 w-4 text-red-600 mr-1" />
                            ) : (salesStats.monthDelta?.deletedUsers || 0) < 0 ? (
                              <TrendingDown className="h-4 w-4 text-green-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={(salesStats.monthDelta?.deletedUsers || 0) > 0 ? 'text-red-600' : (salesStats.monthDelta?.deletedUsers || 0) < 0 ? 'text-green-600' : 'text-gray-600'}>
                              {(salesStats.monthDelta?.deletedUsers || 0) > 0 ? '+' : ''}{salesStats.monthDelta?.deletedUsers || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Enrolled Users */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Successfully Registered</CardDescription>
                      <CardTitle className="text-3xl">{salesStats.enrolledUsers.toLocaleString()}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Week</span>
                          <div className="flex items-center">
                            {salesStats.weekDelta.enrolledUsers > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                            ) : salesStats.weekDelta.enrolledUsers < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={salesStats.weekDelta.enrolledUsers > 0 ? 'text-green-600' : salesStats.weekDelta.enrolledUsers < 0 ? 'text-red-600' : 'text-gray-600'}>
                              {salesStats.weekDelta.enrolledUsers > 0 ? '+' : ''}{salesStats.weekDelta.enrolledUsers}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Month</span>
                          <div className="flex items-center">
                            {salesStats.monthDelta.enrolledUsers > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                            ) : salesStats.monthDelta.enrolledUsers < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={salesStats.monthDelta.enrolledUsers > 0 ? 'text-green-600' : salesStats.monthDelta.enrolledUsers < 0 ? 'text-red-600' : 'text-gray-600'}>
                              {salesStats.monthDelta.enrolledUsers > 0 ? '+' : ''}{salesStats.monthDelta.enrolledUsers}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Active Subscribers */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Active Subscribers</CardDescription>
                      <CardTitle className="text-3xl">{salesStats.activeSubscribers.toLocaleString()}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Week</span>
                          <div className="flex items-center">
                            {salesStats.weekDelta.activeSubscribers > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                            ) : salesStats.weekDelta.activeSubscribers < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={salesStats.weekDelta.activeSubscribers > 0 ? 'text-green-600' : salesStats.weekDelta.activeSubscribers < 0 ? 'text-red-600' : 'text-gray-600'}>
                              {salesStats.weekDelta.activeSubscribers > 0 ? '+' : ''}{salesStats.weekDelta.activeSubscribers}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Last Month</span>
                          <div className="flex items-center">
                            {salesStats.monthDelta.activeSubscribers > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                            ) : salesStats.monthDelta.activeSubscribers < 0 ? (
                              <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                            ) : (
                              <Minus className="h-4 w-4 text-gray-400 mr-1" />
                            )}
                            <span className={salesStats.monthDelta.activeSubscribers > 0 ? 'text-green-600' : salesStats.monthDelta.activeSubscribers < 0 ? 'text-red-600' : 'text-gray-600'}>
                              {salesStats.monthDelta.activeSubscribers > 0 ? '+' : ''}{salesStats.monthDelta.activeSubscribers}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : null}
          </div>
        )}

        <div className={activeSection === 'users' ? 'space-y-4' : 'hidden'}>
          {isRedirectingToUser ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Opening user profile...</p>
            </div>
          ) : (
            <UserList onUserSelect={handleUserSelect} refreshTrigger={refreshTriggers.users} />
          )}
        </div>

        <div className={activeSection === 'disputes' ? 'space-y-4' : 'hidden'}>
          <DisputesReview refreshTrigger={refreshTriggers.disputes} />
        </div>

        <div className={activeSection === 'tradeline-orders' ? 'space-y-4' : 'hidden'}>
          <TradelineOrdersAdmin refreshTrigger={refreshTriggers['tradeline-orders']} />
        </div>

        {activeSection === 'inventory' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Database className="h-5 w-5 mr-2" />
                Inventory Management
              </CardTitle>
              <CardDescription>
                View and sync tradeline inventory from Google Sheets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Button
                  onClick={() => setShowSyncConfirm(true)}
                  disabled={isSyncing}
                >
                  {isSyncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Sync Inventory
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchInventory}
                  disabled={isLoadingInventory}
                >
                  {isLoadingInventory ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
              </div>

              {inventory && (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{inventory.stats.totalTradelines}</p>
                          <p className="text-sm text-muted-foreground">Total Tradelines</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{inventory.stats.availableTradelines}</p>
                          <p className="text-sm text-muted-foreground">Available</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{inventory.stats.totalSlots}</p>
                          <p className="text-sm text-muted-foreground">Total Slots</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{inventory.stats.providers.length}</p>
                          <p className="text-sm text-muted-foreground">Providers</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Last Updated */}
                  {inventory.timestamp && (
                    <div className="text-sm text-muted-foreground">
                      Last updated: {formatDate(new Date(inventory.timestamp))}
                    </div>
                  )}

                  {/* Provider Filter */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Filter by Provider</label>
                    <div className="relative provider-filter-container max-w-md">
                      <button
                        onClick={() => setShowProviderFilter(!showProviderFilter)}
                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        <span className={selectedProviders.length === 0 ? "text-muted-foreground" : ""}>
                          {selectedProviders.length === 0
                            ? "Select providers..."
                            : `${selectedProviders.length} provider${selectedProviders.length > 1 ? 's' : ''} selected`}
                        </span>
                        <div className="flex items-center gap-1">
                          {selectedProviders.length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProviders([]);
                              }}
                              className="hover:bg-muted rounded-sm p-0.5 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </div>
                      </button>
                      
                      {showProviderFilter && (
                        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                          <div className="max-h-[300px] overflow-y-auto p-1">
                            {inventory.stats.providers.map((provider) => (
                              <div
                                key={provider}
                                onClick={() => toggleProvider(provider)}
                                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                              >
                                <div className="flex h-4 w-4 items-center justify-center mr-2">
                                  {selectedProviders.includes(provider) && (
                                    <Check className="h-4 w-4" />
                                  )}
                                </div>
                                <span>{provider}</span>
                              </div>
                            ))}
                          </div>
                          {selectedProviders.length > 0 && (
                            <div className="border-t p-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProviders([]);
                                }}
                                className="w-full px-2 py-1.5 text-sm rounded-md text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                Clear all filters
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedProviders.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Showing {filteredInventory.length} of {inventory.inventory.length} tradelines
                      </p>
                    )}
                  </div>

                  {/* Inventory Table */}
                  {filteredInventory.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Card Name</TableHead>
                            <TableHead>Provider</TableHead>
                            <TableHead>Open Date</TableHead>
                            <TableHead>Credit Limit</TableHead>
                            <TableHead>Cost</TableHead>
                            <TableHead>Available Slots</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredInventory.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div>{item.card_name}</div>
                                <div className="text-xs text-gray-500">{item.id}</div>
                              </TableCell>
                              <TableCell className="font-medium">{item.provider}</TableCell>
                              <TableCell>
                                {isAmexTradeline(item.provider, item.card_name) ? '-' : formatDate(item.open_date)}
                              </TableCell>
                              <TableCell>{formatCurrency(item.credit_limit)}</TableCell>
                              <TableCell>{formatCurrency(item.cost_per_tradeline)}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  item.available_slots > 0 
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {item.available_slots}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : selectedProviders.length > 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No tradelines found for the selected providers.</p>
                      <Button
                        variant="link"
                        onClick={() => setSelectedProviders([])}
                        className="mt-2"
                      >
                        Clear filters
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Status Messages */}
        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog for Sync Inventory */}
      <Dialog open={showSyncConfirm} onOpenChange={setShowSyncConfirm}>
        <DialogContent className="sm:max-w-[500px]">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Confirm Inventory Sync
              </h2>
              <div className="space-y-2 mt-3">
                <p className="text-sm text-gray-600">
                  You are about to sync tradeline inventory data from <strong>Google Sheets</strong>.
                </p>
                <p className="text-sm text-amber-600 font-medium">
                  This will update the inventory database and may affect availability of tradelines.
                </p>
                <p className="text-sm text-gray-600">Are you sure you want to continue?</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSyncConfirm(false)}
                disabled={isSyncing}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={syncInventory}
                disabled={isSyncing}
                className="bg-black hover:bg-black/90 text-white px-4"
              >
                {isSyncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Yes, Sync Inventory
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

